import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { InAppNotification } from '../api/Types';
import useHeaderNotificationsMenu from '../hooks/notifications/useHeaderNotificationsMenu';
import { Box, IconButton, Text } from './ui';

interface HeaderNotificationsProps {
  notifications: InAppNotification[];
  unreadCount: number;
  loading?: boolean;
  onMenuOpened?: () => void | Promise<void>;
  onNotificationClick?: (notification: InAppNotification) => void;
}

const formatUnreadCountLabel = (unreadCount: number): string =>
  `Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ''}`;

const formatUnreadCountBadge = (unreadCount: number): string | number =>
  unreadCount > 99 ? '99+' : unreadCount;

const getNotificationItemClassName = (notification: InAppNotification): string =>
  `header-notification-item ${notification.is_read ? 'is-read' : 'is-unread'}`;

const formatNotificationDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const HeaderNotifications: React.FC<HeaderNotificationsProps> = ({
  notifications,
  unreadCount,
  loading = false,
  onMenuOpened,
  onNotificationClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    isOpen,
    closeMenu,
    toggleMenu,
  } = useHeaderNotificationsMenu({
    containerRef,
    onMenuOpened,
  });

  const handleNotificationClick = (notification: InAppNotification) => {
    onNotificationClick?.(notification);
    closeMenu();
  };

  return (
    <Box ref={containerRef} className="header-notifications-container">
      <IconButton
        label={formatUnreadCountLabel(unreadCount)}
        type="button"
        variant="link"
        radius={false}
        textColor="white"
        className="header-notifications-button"
        onClick={toggleMenu}
        aria-expanded={isOpen}
      >
        <Box as="span" className="header-notifications-icon" aria-hidden="true">🔔</Box>
        {unreadCount > 0 && (
          <Box as="span" className="header-notifications-badge" aria-hidden="true">
            {formatUnreadCountBadge(unreadCount)}
          </Box>
        )}
      </IconButton>

      {isOpen && (
        <Box className="header-notifications-menu" shadow>
          <Box className="header-notifications-menu-header">
            Notifications
          </Box>
          <Box className="header-notifications-menu-body">
            {loading ? (
              <Text as="div" className="header-notifications-empty">Chargement...</Text>
            ) : notifications.length === 0 ? (
              <Text as="div" className="header-notifications-empty">Aucune notification.</Text>
            ) : (
              notifications.map((notification) => (
                <Link
                  key={notification.id}
                  to={notification.route_path || '/'}
                  className={getNotificationItemClassName(notification)}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <Box className="header-notification-item-title">{notification.title}</Box>
                  {notification.body ? (
                    <Box className="header-notification-item-body">{notification.body}</Box>
                  ) : null}
                  <Box className="header-notification-item-date">
                    {formatNotificationDate(notification.created_at)}
                  </Box>
                </Link>
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HeaderNotifications;
